import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import AddGiftCardsDropZone from "../../app-specific/dropzone/add-gift-cards";
import {readString} from "react-papaparse";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import {exportCSVFile, formatMoney} from "../../../../core/services/utils/utils";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {DialogActions, DialogContent, DialogTitle, Fade, IconButton} from "@mui/material";
import currency from 'currency.js';
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import {BulkImportGiftCardsDataGridCellKeys} from "../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import PartnerDataGrid from "../../../containers/data-grid";
import ToastService from "../../../../core/services/toast";
import {Link, useHistory} from "react-router-dom";
import {routes} from "../../../../core/models/static/routes/app-routes";
import {OpenInNew, WarningOutlined} from "@mui/icons-material";
import {MuiRFF, Yup} from "../../../components/form";

const cardNumberIdentifier = 'card-number-';
const amountIdentifier = 'amount-';
const categoryIdentifier = 'category-';
const checkboxIdentifier = 'checkbox-';


const dataGridSaveConfig = {
    name: 'ImportGiftCardsDialogDataGrid',
    version: 1,
}


const ImportGiftCardsDialog = ({
                                   open,
                                   setOpen,
                                   giftCardCategories: giftCardCategoriesProp,
                                   onSuccess
                               }) => {
    const history = useHistory();
    const [parsing, setParsing] = useState(false);
    const [parseError, setParseError] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [submittedOnce, setSubmittedOnce] = useState(false);
    const [downloadingSample, setDownloadingSample] = useState(false);
    const [parsedGiftCards, setParsedGiftCards] = useState(null);
    const [giftCardCategories, setGiftCardCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false)
    const isMounted = useIsMounted();


    /**
     * Memo version of Yup schema for the form of parse gift cards
     * @type {unknown}
     */
    const schema = useMemo(() => {
        if (!parsedGiftCards || parsedGiftCards.length < 1)
            return Yup.object().shape({});
        let resSchema = {};
        parsedGiftCards?.forEach(e => {
            resSchema[`${cardNumberIdentifier}${e.id}`] = Yup.string().nullable().required(ValidateMessages.required);
            resSchema[`${checkboxIdentifier}${e.id}`] = Yup.boolean().nullable().required(ValidateMessages.required);
            resSchema[`${amountIdentifier}${e.id}`] = Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('Number'));
            resSchema[`${categoryIdentifier}${e.id}`] = Yup.object().nullable();
        })
        return Yup.object().shape(resSchema)
    }, [parsedGiftCards])

    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);
    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);

    /**
     * Memo version of initial values
     * @type {any}
     */
    const initialValues = useMemo(() => {
        let res = {};
        parsedGiftCards?.forEach(e => {
            res[`${cardNumberIdentifier}${e.id}`] = e.cardNumber;
            res[`${checkboxIdentifier}${e.id}`] = e.selected;
            res[`${amountIdentifier}${e.id}`] = e.amount;
            res[`${categoryIdentifier}${e.id}`] = giftCardCategories.find(c => c.name.toLowerCase().trim() === e.category?.toLowerCase()?.trim() ?? '');
        })
        return res;
    }, [giftCardCategories, parsedGiftCards])

    /**
     * Fetches the list of gift card categories from the server and if the result of the api is successful, sets the
     * list.
     * @type {function(): Promise<void>}
     */
    const getGiftCardCategories = useCallback(async () => {
        setLoadingCategories(true);
        let response = await Api.getGiftCardCategories();
        if (!isMounted())
            return;
        setLoadingCategories(false);
        if (response?.resultFlag)
            setGiftCardCategories(response.data);
    }, [isMounted])

    /**
     * Loads the gift card categories as soon as the component mounts
     */
    useEffect(() => {
        if (!open)
            return;
        if (!giftCardCategoriesProp) {
            void getGiftCardCategories();
        } else {
            setGiftCardCategories(giftCardCategoriesProp ?? []);
        }
    }, [open, getGiftCardCategories, giftCardCategoriesProp])

    /**
     * Listens to the changes in whether the modal is open or not and if not, resets the states to their previous state.
     */
    useEffect(() => {
        if (!open) {
            setParsedGiftCards(null);
            setParsing(false);
            setSubmittedOnce(false);
        }
    }, [open]);

    /**
     * Listens to the changes in whether the modal is open or not and if not, resets the states to their previous state.
     */
    useEffect(() => {
        if (!parsedGiftCards?.length) {
            setParsing(false);
            setSubmittedOnce(false);
        }
    }, [parsedGiftCards]);


    /**
     * Parses the Gift Cards from the dropped Csv File
     * @type {function(*): void}
     */
    const parseGiftCards = useCallback(([csvFile]) => {
        try {
            const parsed = readString(csvFile, {header: false});
            if (parsed.data?.length < 2) {
                setParseError('The chosen CSV File does not contain any values.');
                return;
            }
            const giftCards = parsed?.data
                .filter((e) => e?.length === 3)
                ?.filter((e, index) => index !== 0)
                ?.map(([cardNumber, amount, category], index) => ({
                    cardNumber: cardNumber,
                    amount: currency(amount),
                    id: index,
                    selected: true,
                    category: !!category ? category : undefined,
                }));
            if (giftCards.length) setParsedGiftCards(giftCards);
        } catch (e) {
            setParseError('Error While Parsing the given CSV File.');
        }
    }, []);

    /**
     * Adds a list of new gift cards by calling the api server and if the result was successful, calls the onSuccess
     * callback with the newly created gift cards list as its argument.
     * The api may return a list of unsuccessful submissions, which in case they shall not be removed from the
     * selection list.
     * @param giftCards {any[]} gift cards
     */
    const addGiftCards = useCallback(async (giftCards) => {
        if (!giftCards?.length)
            return;
        if (!giftCardCategories?.length) {
            ToastService.error({
                title: 'Import Failed!',
                message: 'There seems to be no gift card categories available. Please create one before importing any gift cards into' +
                    ' the system.',
                buttons: [
                    {
                        children: 'Categories Page',
                        onClick: (toastProps) => {
                            toastProps?.closeToast?.();
                            history.push(routes.settings.giftCards.categories);
                        },
                    }
                ],
            })
            return;
        }
        setSubmitting(true);
        const forApi = giftCards?.map(e => ({...e, amount: parseFloat(e.amount), isActive: true,}));
        const response = await Api.createGiftCardInBulk(forApi);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            if (response?.data?.length) {
                // case of some errors
                const erroneousCards = response?.data?.filter(e => !e.resultFlag) ?? [];
                const successfulCards = []
                for (const creditCard of forApi) {
                    const found = erroneousCards?.map(e => e.data)?.find(e => e.id === creditCard.id)
                    if (!found) {
                        successfulCards.push(creditCard)
                    }
                }
                if (successfulCards?.length)
                    onSuccess(successfulCards);
                const currentParsed = []
                //Setting gift cards with error as currently parse gift cards
                for (const parsedGiftCard of parsedGiftCards) {
                    const found = erroneousCards?.find(e => e.data?.id === parsedGiftCard?.id)
                    if (found)
                        currentParsed.push({
                            ...parsedGiftCard,
                            message: found.message,
                        })
                }
                setParsedGiftCards(currentParsed);
            } else {
                // all cards accepted successfully
                setOpen(false);
            }
        }
        if (!submittedOnce) {
            setSubmittedOnce(true);
        }
        setSubmitting(false);
    }, [giftCardCategories?.length, history, isMounted, onSuccess, parsedGiftCards, setOpen, submittedOnce])

    /**
     * For the selected gift cards, creates a list, and calls addGiftCards with these gift cards as its argument.
     * Discards the unselected Gift cards.
     * @param values
     */
    const submitGiftCards = useCallback((values) => {
        const res = [];
        const toBeRemoved = [];
        for (const [key, value] of Object.entries(values)) {
            if (key.match(RegExp(`^${checkboxIdentifier}\\S`))) {
                const id = parseInt(key.replace(checkboxIdentifier, ''));
                if (!value) {
                    toBeRemoved.push({
                        cardNumber: values[`${cardNumberIdentifier}${id}`],
                        amount: values[`${amountIdentifier}${id}`],
                        category: values[`${categoryIdentifier}${id}`],
                        id: id,
                    })
                } else {
                    res.push({
                        cardNumber: values[`${cardNumberIdentifier}${id}`],
                        amount: values[`${amountIdentifier}${id}`],
                        category: values[`${categoryIdentifier}${id}`],
                        id: id,
                    });
                }
            }
        }
        if (res.length)
            addGiftCards(res);
        if (toBeRemoved.length) {
            const toBeRemovedIds = toBeRemoved.map(e => e.id);
            setParsedGiftCards(prevState => prevState.filter(e => !toBeRemovedIds.includes(e.id)));
        }
        if (!res.length)
            setOpen(false);
    }, [addGiftCards, setOpen])

    /**
     * Creates a sample of the svg for importing gift card svg and downloads it on users device
     */
    const downloadSample = useCallback(() => {
        setDownloadingSample(true);
        const sampleData = [];
        for (let index = 1; index < 11; ++index) {
            let catIndex;
            if (!giftCardCategories.length) catIndex = -1
            else {
                catIndex = index % giftCardCategories.length
            }
            sampleData.push({
                'Card Number': '123456789123',
                'Amount': formatMoney(100 * index),
                "Category": catIndex === -1
                    ? "Default"
                    : giftCardCategories[catIndex].name
            });
        }
        exportCSVFile(sampleData, 'Sample Gift Card Import')
        setDownloadingSample(false);
    }, [giftCardCategories])

    /**
     * Memo version of data grid columns
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: BulkImportGiftCardsDataGridCellKeys.selectBox,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 0,
            },
            {
                title: "Card Number",
                sortable: false,
                name: BulkImportGiftCardsDataGridCellKeys.cardNumber,
                alignment: DataGridColumnAlignments.left,
                type: submittedOnce ? DataGridColumnTypes.element : DataGridColumnTypes.string,
                width: !submittedOnce ? 200 : 150,
            },
            {
                title: "Amount",
                sortable: false,
                name: BulkImportGiftCardsDataGridCellKeys.amount,
                alignment: DataGridColumnAlignments.right,
                type: submittedOnce ? DataGridColumnTypes.element : DataGridColumnTypes.money,
                width: !submittedOnce ? 80 : 120,
            },
            {
                title: "Category",
                sortable: false,
                name: BulkImportGiftCardsDataGridCellKeys.category,
                alignment: DataGridColumnAlignments.left,
                type: submittedOnce ? DataGridColumnTypes.element : DataGridColumnTypes.string,
                width: 200,
            },
            ...(
                submittedOnce ? [{
                    title: "Message",
                    name: BulkImportGiftCardsDataGridCellKeys.errorMessage,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: !submittedOnce ? 0 : 300,
                }] : []
            ),
        ], [submittedOnce])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of call back function for generating the data grid rows
     * @type {function(*, *): *}
     */
    const rows = useCallback((values, form) => {
        return parsedGiftCards?.map(giftCard => ({
            key: giftCard.id,
            data: giftCard,
            cells: {
                [BulkImportGiftCardsDataGridCellKeys.selectBox]:
                    <CheckboxForm
                        className={'m-0 p-0 ml-3'}
                        name={`${checkboxIdentifier}${giftCard.id}`}
                        required={required[`${checkboxIdentifier}${giftCard.id}`]}
                        data={{value: values[`${checkboxIdentifier}${giftCard.id}`]}}
                    />,
                [BulkImportGiftCardsDataGridCellKeys.cardNumber]:
                    !submittedOnce
                        ? giftCard.cardNumber
                        : <OutlinedInputForm
                            size={'small'}

                            name={`${cardNumberIdentifier}${giftCard.id}`}
                            required={required[`${cardNumberIdentifier}${giftCard.id}`]}
                            value={values[`${cardNumberIdentifier}${giftCard.id}`] ?? ''}
                            onChange={(e) => form.change(e.target.name, e.target.value)}
                        />,
                [BulkImportGiftCardsDataGridCellKeys.amount]:
                    !submittedOnce
                        ? giftCard.amount
                        : <OutlinedInputForm
                            size={'small'}
                            className={'px-2'}

                            name={`${amountIdentifier}${giftCard.id}`}
                            required={required[`${amountIdentifier}${giftCard.id}`]}
                            value={values[`${amountIdentifier}${giftCard.id}`] ?? ''}
                            onChange={(e) => form.change(e.target.name, e.target.value)}
                        />,
                [BulkImportGiftCardsDataGridCellKeys.category]:
                    !submittedOnce
                        ? giftCard.category
                        : <OutlinedDropdownForm
                            size={'small'}
                            name={`${categoryIdentifier}${giftCard.id}`}
                            required={required[`${categoryIdentifier}${giftCard.id}`]}
                            value={values[`${categoryIdentifier}${giftCard.id}`] ?? ''}
                            onChange={(e) => form.change(e.target.name, e.target.value)}
                            data={giftCardCategories?.map(e => ({
                                value: e,
                                label: e.name,
                                id: e.id
                            })) ?? []}
                        />,
                ...(submittedOnce ? {
                    [BulkImportGiftCardsDataGridCellKeys.errorMessage]:
                        !!giftCard.message
                            ? <p {...dataGridCellTextProps} className={'text-sm text-wrap '}>
                                {giftCard.message}
                            </p>
                            : <></>
                } : [])
            }
        }))
    }, [giftCardCategories, parsedGiftCards, required, submittedOnce])


    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={parsedGiftCards?.length > 0 ? 'md' : 'sm'}
            loading={submitting}
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
            className={'bound-dialog import-gift-cards-dialogs'}
        >
            <DialogTitle id="scroll-dialog-title">
                <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                    Import Gift Cards
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent>
                {
                    parsedGiftCards?.length > 0
                        ? (
                            <Form
                                id={'submission-form'}
                                validate={validate}
                                initialValues={initialValues}
                                className='w-100'
                                onSubmit={submitGiftCards}
                                render={({values, form}) => (
                                    <Row className={'w-100 no-gutters'}>
                                        <Row className='no-gutters w-100 px-3'>
                                            <Fade in={submittedOnce} mountOnEnter unmountOnExit>
                                                <div className={'mt-3 p-3 background-red-2 w-100 rounded-xxl'}>
                                                    <p className={'text-sm text-color-1 text-wrap text-center '}>
                                                        The following gift cards seems to have been corrupted. Please edit
                                                        them if needed and submit the form again.
                                                    </p>
                                                </div>
                                            </Fade>
                                        </Row>
                                        <PartnerDataGrid
                                            {...dataGridSaveConfig}
                                            initialLayoutState={{
                                                columns: columns,
                                            }}
                                            columns={dataGridColumns}
                                            rows={rows(values, form)}
                                            classNames={{
                                                toolbar: 'data-grid-tool-bar'
                                            }}
                                            visibleRows={submittedOnce ? 6 : 8}
                                            hideFooter
                                            hideExporting
                                            state={{
                                                loading: {state: submitting},
                                                density: dataGridDensity,
                                            }}
                                        />
                                        <div className='my-2 w-100'/>
                                    </Row>
                                )}
                            />
                        )
                        : (
                            <div className={'d-flex flex-column p-3'}>
                                {
                                    !loadingCategories && !giftCardCategories?.length &&
                                    <Col xs={12} className='px-2 mb-3'>
                                        <p className={'text-sm text-color-3 text-center'}>
                                            <WarningOutlined
                                                color={'warning'}
                                                sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                                            />
                                            There are no categories to select. Please create a category first.
                                            <IconButton
                                                color={'paper.primary'}
                                                component={Link}
                                                to={routes.products.categoryManagement}
                                                target={'_blank'}
                                                sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                                onClick={() => setOpen(false)}

                                            >
                                                <OpenInNew/>
                                            </IconButton>
                                        </p>
                                    </Col>
                                }
                                <div className={'m-3'}>
                                    <AddGiftCardsDropZone
                                        uploading={parsing}
                                        disabled={parsedGiftCards?.length > 0}
                                        onSuccess={parseGiftCards}
                                        error={parseError}
                                    />
                                </div>
                            </div>
                        )
                }
            </DialogContent>
            <Divider className='divider'/>
            <DialogActions>
                {
                    parsedGiftCards?.length > 0
                        ? <>
                            <Row className='no-gutters justify-content-between align-items-start w-100'>
                                <Button
                                    disabled={!open || submitting}
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={() => setOpen(false)}
                                    borderRadius={'0 8px 0 8px'}
                                >
                                    {submitting ? 'submitting...' : 'Close'}
                                </Button>
                                <Button
                                    disabled={!open || submitting}
                                    variant={'dialog'}
                                    color={'primary'}
                                    type={'submit'}
                                    form={'submission-form'}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    {submitting ? 'submitting...' : 'Submit'}
                                </Button>
                            </Row>
                        </>
                        : <>
                            <Row className='no-gutters justify-content-between align-items-start w-100'>
                                <Button
                                    disabled={!open || submitting}
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={() => setOpen(false)}
                                    borderRadius={'0 8px 0 8px'}
                                >
                                    {submitting ? 'submitting...' : 'Close'}
                                </Button>
                                <Button
                                    disabled={!open || submitting || downloadingSample}
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={downloadSample}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    {downloadingSample ? 'Downloading...' : submitting ? 'submitting...' : 'Download Sample'}
                                </Button>
                            </Row>
                        </>
                }
            </DialogActions>

        </Dialog>
    );
}

export default ImportGiftCardsDialog;


